/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";

import ToggleSwitch from "../../../assets/component/ToggleSwitch/ToggleSwitch.js";
import { renderErrorMsg } from "../../../assets/component/renderErrorMsg.js";
import { sortArrayByValue, stringToBoolean, arrayMove } from "../../../assets/helper.js";
import UpgradeNoticeCard from "./UpgradeNoticeCard";

const defaultFormData = [
  {
    id: 'last_name',
    type: 'text',
    format: 'text',
    required: true,
    mandatory: true,
    showOnReceipt: true,
    order: 1,
  },
  {
    id: 'first_name',
    type: 'text',
    format: 'text',
    required: true,
    mandatory: true,
    showOnReceipt: true,
    order: 2,
  },
  {
    id: 'email',
    type: 'text',
    format: 'text',
    required: true,
    mandatory: true,
    showOnReceipt: true,
    order: 3,
  },
  {
    id: 'contact',
    type: 'text',
    format: 'text',
    required: true,
    mandatory: true,
    showOnReceipt: true,
    order: 4,
  },
];

class RenderCustomInputFields extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isEditMode: false,
      defaultFields: [],
      customFields: [],
    };

    this.mapDefaultFields = this.mapDefaultFields.bind(this);
    this.mapCustomFieldsFromProps = this.mapCustomFieldsFromProps.bind(this);

    this.handleToggleEditMode = this.handleToggleEditMode.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleAddField = this.handleAddField.bind(this);
    this.handleArchiveField = this.handleArchiveField.bind(this);
    this.handleDeleteField = this.handleDeleteField.bind(this);
    this.handleActionField = this.handleActionField.bind(this);
    this.handleOrderField = this.handleOrderField.bind(this);
    this.handleAddOption = this.handleAddOption.bind(this);
    this.handleRemoveOption = this.handleRemoveOption.bind(this);
    this.handleOptionLabelChange = this.handleOptionLabelChange.bind(this);

    this.renderDefaultFields = this.renderDefaultFields.bind(this);
    this.renderCustomFields = this.renderCustomFields.bind(this);
    this.renderErrorMsg = this.renderErrorMsg.bind(this);
  }

  componentDidMount() {
    const defaultFields = this.mapDefaultFields();
    const customFields = this.mapCustomFieldsFromProps();

    this.setState({
      defaultFields,
      customFields,
    });
  }

  mapDefaultFields() {
    const newDefaultFields = defaultFormData.map(item => {
      let label = {
        en: 'Label',
        cn: 'Label',
        zh: 'Label',
      };

      let placeholder = {
        en: 'Placeholder',
        cn: 'Placeholder',
        zh: 'Placeholder',
      };

      // TO DO: update value for each language using languageObj
      switch (item.id) {
        case 'last_name':
          label = {
            en: 'Last Name',
            cn: 'Last Name CN',
            zh: 'Last Name ZH',
          };
          placeholder = {
            en: 'Last Name',
            cn: 'Last Name CN',
            zh: 'Last Name ZH',
          };
          break;
        case 'first_name':
          label = {
            en: 'First Name',
            cn: 'First Name CN',
            zh: 'First Name ZH',
          };
          placeholder = {
            en: 'First Name',
            cn: 'First Name CN',
            zh: 'First Name ZH',
          };
          break;
        case 'email':
          label = {
            en: 'Email',
            cn: 'Email CN',
            zh: 'Email ZH',
          };
          placeholder = {
            en: 'Email',
            cn: 'Email CN',
            zh: 'Email ZH',
          };
          break;
        case 'contact':
          label = {
            en: 'Contact',
            cn: 'Contact CN',
            zh: 'Contact ZH',
          };
          placeholder = {
            en: 'Contact',
            cn: 'Contact CN',
            zh: 'Contact ZH',
          };
          break;
        default:
          break;
      }

      return {
        ...item,
        i18n: {
          label,
          placeholder
        },
      };
    });

    return newDefaultFields;
  }

  mapCustomFieldsFromProps() {
    const { state } = this.props;
    const { customFields } = this.state;

    let newCustomFields = customFields;

    if (state.organizationObj.formSetting) {
      const getCustomFields = state.organizationObj.formSetting.rows || [];

      if (getCustomFields.length > 0) {
        newCustomFields = sortArrayByValue(getCustomFields, 'order', 'asc');
      }
    }

    return newCustomFields
  }

  handleToggleEditMode() {
    const { isEditMode } = this.state;

    const newState = !isEditMode;

    if (newState === false) {
      // reset form data when cancel edit
      const newFormData = this.mapCustomFieldsFromProps();

      this.setState({
        customFields: newFormData,
      });
    }

    this.setState({
      isEditMode: newState,
    });
  }

  handleSave() {
    const { customFields } = this.state;
    const { func } = this.props;

    func.handleUpdateCustomFields(customFields);

    this.setState({ isEditMode: false });
  }

  handleInputChange(e, fieldId) {
    const { name, value } = e.target;
    const lang = e._lang || this.props.language;
    const { customFields } = this.state;

    const newFormData = customFields.map((item) => {
      if (item.id === fieldId) {
        return {
          ...item,
          i18n: {
            ...item.i18n,
            [name]: {
              ...item.i18n[name],
              [lang]: value,
            },
          },
        };
      }

      return item;
    });

    this.setState({
      customFields: newFormData,
    });
  }

  handleAddField() {
    const { customFields } = this.state;

    let newOrder = 1;
    if (customFields.length > 0) {
      newOrder = customFields[customFields.length - 1].order + 1;
    }

    const newId = `field_${Date.now()}`;

    const newField = {
      id: newId,
      type: 'text',
      format: 'text',
      required: false,
      mandatory: false,
      showOnReceipt: false,
      internal: false,
      order: newOrder,
      isNew: true,
      i18n: {
        label: {
          en: '',
        },
        placeholder: {
          en: '',
        },
      },
    };

    this.setState(prevState => ({
      customFields: [...prevState.customFields, newField],
    }));

    setTimeout(() => {
      if (document) {
        const view = document.getElementById(`label_${newId}`);

        if (view) {
          view.focus();
        }
      }
    }, 100)
  }

  handleArchiveField(fieldId, value) {
    const { isEditMode, customFields } = this.state;

    if (isEditMode) {
      const newFormData = customFields.map((item) => {
        if (item.id === fieldId) {
          return {
            ...item,
            archived: value,
          };
        }
  
        return item;
      });
  
      this.setState({
        customFields: newFormData,
      });
    }
  }

  handleDeleteField(fieldId) {
    const { customFields } = this.state;

    let newFormData = customFields.filter(item => item.id !== fieldId);

    // update order value using new index
    newFormData = newFormData.map((item, index) => {
      return {
        ...item,
        order: index + 1,
      };
    });

    this.setState({
      customFields: newFormData,
    });
  }

  handleActionField(fieldId, key, value) {
    const { isEditMode, customFields } = this.state;

    if (isEditMode) {
      let formattedValue = value;
      if (key === 'required') {
        formattedValue = stringToBoolean(value);
      }
  
      const newFormData = customFields.map((item) => {
        if (item.id === fieldId) {
          return {
            ...item,
            [key]: formattedValue,
          };
        }
  
        return item;
      });
  
      this.setState({
        customFields: newFormData,
      });
    }
  }

  handleOrderField(fieldId, type) {
    const { customFields } = this.state;

    const getIndex = customFields.findIndex(item => item.id === fieldId);

    if (type === 'up' && getIndex - 1 < 0) {
      return;
    } else if (type === 'down' && getIndex + 1 >= customFields.length) {
      return;
    }

    const newIndex = type === 'up' ? getIndex - 1 : getIndex + 1;

    let newFormData = arrayMove(customFields, getIndex, newIndex);

    // update order value using new index
    newFormData = newFormData.map((item, index) => {
      return {
        ...item,
        order: index + 1,
      };
    });

    this.setState({
      customFields: newFormData,
    });
  }

  handleAddOption(fieldId) {
    const { customFields } = this.state;
    const newFormData = customFields.map((item) => {
      if (item.id === fieldId) {
        const options = item.options || [];
        return {
          ...item,
          options: [...options, { id: `opt_${Date.now()}`, label: { en: '' } }],
        };
      }
      return item;
    });
    this.setState({ customFields: newFormData });
  }

  handleRemoveOption(fieldId, optionId) {
    const { customFields } = this.state;
    const newFormData = customFields.map((item) => {
      if (item.id === fieldId) {
        return {
          ...item,
          options: (item.options || []).filter(opt => opt.id !== optionId),
        };
      }
      return item;
    });
    this.setState({ customFields: newFormData });
  }

  handleOptionLabelChange(fieldId, optionId, language, value) {
    const { customFields } = this.state;
    const newFormData = customFields.map((item) => {
      if (item.id === fieldId) {
        return {
          ...item,
          options: (item.options || []).map(opt => {
            if (opt.id === optionId) {
              return { ...opt, label: { ...opt.label, [language]: value } };
            }
            return opt;
          }),
        };
      }
      return item;
    });
    this.setState({ customFields: newFormData });
  }

  renderDefaultFields() {
    const { language } = this.props;
    const { defaultFields, isEditMode } = this.state;

    return defaultFields.map(item => (
      <RenderFormInput
        key={item.id}
        language={language}
        languageObj={this.props.languageObj || {}}
        allData={defaultFields}
        data={item}
        isEditable={false}
        isEditMode={isEditMode}
      />
    ));
  }

  renderCustomFields() {
    const { language } = this.props;
    const { customFields, isEditMode } = this.state;

    return customFields.map(item => (
      <RenderFormInput
        key={item.id}
        language={language}
        languageObj={this.props.languageObj || {}}
        allData={customFields}
        data={item}
        isEditable={true}
        isEditMode={isEditMode}
        handleInputChange={(e, fieldId) => this.handleInputChange(e, fieldId)}
        handleArchiveField={(fieldId, value) => this.handleArchiveField(fieldId, value)}
        handleDeleteField={fieldId => this.handleDeleteField(fieldId)}
        handleActionField={(fieldId, key, value) => this.handleActionField(fieldId, key, value)}
        handleOrderField={(fieldId, type) => this.handleOrderField(fieldId, type)}
        handleAddOption={(fieldId) => this.handleAddOption(fieldId)}
        handleRemoveOption={(fieldId, optionId) => this.handleRemoveOption(fieldId, optionId)}
        handleOptionLabelChange={(fieldId, optionId, lang, value) => this.handleOptionLabelChange(fieldId, optionId, lang, value)}
      />
    ));
  }

  renderErrorMsg() {
    const { state } = this.props;
    const errMsg = state.errMsgInfo.customFields;

    if (errMsg.msg !== "") {
      return (
        <div className="RenderCustomInputFields-errorContainer">
          {renderErrorMsg(errMsg.msg)}
        </div>
      );
    }

    return null;
  }

  render() {
    const { state, func } = this.props;
    const { isEditMode, customFields } = this.state;

    let formEnabled = false;
    if (state.organizationObj.formSetting && state.organizationObj.formSetting.enabled) {
      formEnabled = state.organizationObj.formSetting.enabled;
    }

    if (state.loading) {
      return (
        <div className="OrgInfo-emptyPage">
          <div className="OrgInfo-center">
            <div className="OrgInfo-btm-load"></div>
          </div>
        </div>
      );
    }

    const currentPlan = state.organizationObj.currentPlan
      ? state.organizationObj.currentPlan.toLowerCase()
      : "";

    if (currentPlan !== "premium") {
      return (
        <div className="OrgInfo-contentContainer">
          <div className="OrgInfo-contentHeader">
            <div className="RenderCustomInputFields-headerInfo">{(this.props.languageObj && this.props.languageObj.heading) || "Custom Input Fields"}</div>
          </div>
          <div className="OrgInfo-content flex column">
            <UpgradeNoticeCard
              heading={(this.props.languageObj && this.props.languageObj.upgradeHeading) || "Unlock Custom Input Fields"}
              body={(this.props.languageObj && this.props.languageObj.upgradeBody) || "Custom input fields are available on the Premium plan. Upgrade to collect additional details from your supporters."}
              onUpgradeClick={func.redirectToOrgBilling}
            />
          </div>
          <div className="OrgInfo-footer" />
        </div>
      );
    }

    return (
      <div className="OrgInfo-contentContainer">
        <div className="OrgInfo-contentHeader">
          <div className="RenderCustomInputFields-headerInfo">{(this.props.languageObj && this.props.languageObj.heading) || "Custom Input Fields"}</div>

          {!isEditMode && customFields.length > 0 && (
            <div className="RenderCustomInputFields-toggleContainer">
              <ToggleSwitch
                checked={formEnabled}
                onChange={func.handleUpdateCustomFieldsStatus}
              />
            </div>
          )}

          <div onClick={this.handleToggleEditMode}>
            {isEditMode ? <div className="RenderCustomInputFields-cancelBtn">{(this.props.languageObj && this.props.languageObj.cancel) || "Cancel"}</div>  : <div className="RenderCustomInputFields-submitBtn">{(this.props.languageObj && this.props.languageObj.edit) || "Edit"}</div> }
          </div>

          {isEditMode && (
            <div className="RenderCustomInputFields-submitBtn" onClick={this.handleSave}>{(this.props.languageObj && this.props.languageObj.save) || "Save"}</div>
          )}
        </div>

        <div className="RenderCustomInputFields-content">

          <div className="RenderCustomInputFields-contentHeader">
            {isEditMode && (
              <div onClick={this.handleAddField} className="RenderCustomInputFields-addBtn">
                <div className="RenderCustomInputFields-addIcn" />
              </div>
            )}

            <div className={`RenderCustomInputFields-infoContainer ${!isEditMode ? "marginLeft" : "marginTop"}`}>
              <div className="RenderCustomInputFields-infoIcn" />
              <div className="RenderCustomInputFields-infoText">
                {(this.props.languageObj && this.props.languageObj.receiptInfo) || "Information with the receipt icon will be displayed on receipts."}
              </div>
            </div>

          </div>

          {this.renderErrorMsg()}

          {this.renderDefaultFields()}
          {this.renderCustomFields()}

        </div>
        <div className="OrgInfo-footer" />
      </div>
    );
  }
}

function RenderFormInput(props) {
  const {
    language,
    languageObj = {},
    allData,
    data,
    isEditable,
    isEditMode,
    handleInputChange = () => {},
    handleArchiveField = () => {},
    handleDeleteField = () => {},
    handleActionField = () => {},
    handleOrderField = () => {},
    handleAddOption = () => {},
    handleRemoveOption = () => {},
    handleOptionLabelChange = () => {},
  } = props;

  const t = (key, fallback) => languageObj[key] || fallback;

  const isShowBtnUp = data.order !== 1;
  const isShowBtnDown = data.order !== allData.length;

  const isArchived = data.archived;
  const isInputDisabled = !isEditMode || !isEditable;

  const classArchived = isEditMode && isArchived ? 'archived' : '';
  const classInputDisabled = !isEditMode ? 'disabled' : '';
  const classInactive = !isEditMode ? 'inactive' : '';
  const classReceipt = !data.showOnReceipt ? 'gray' : '';
  const classActionDisabled = isEditMode && !isEditable ? 'disabled' : '';

  const labelValue = data.i18n.label[language] || (!data.isNew ? data.i18n.label.en : '');
  const placeholderValue = data.i18n.placeholder[language] || (!data.isNew ? data.i18n.placeholder.en : '');

  if (!isEditMode && isArchived) return null;

  const renderOrderBtn = () => {
    if (isEditable && isEditMode) {
      return (
        <div
          className={`
            RenderCustomInputFields-formOrderContainer 
            ${!isShowBtnUp && !isShowBtnDown ? 'hide' : ''}
          `}
        >
          {isShowBtnUp && (
            <div 
              className={`
                RenderCustomInputFields-formOrderBtn up 
                ${!isShowBtnDown ? 'noMargin' : ''}
              `}
              onClick={() => handleOrderField(data.id, 'up')}
            >
              <div className="RenderCustomInputFields-formOrderUpIcn" />
            </div>
          )}
          {isShowBtnDown && (
            <div
              className="RenderCustomInputFields-formOrderBtn down"
              onClick={() => handleOrderField(data.id, 'down')}
            >
              <div className="RenderCustomInputFields-formOrderDownIcn" />
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const [editLang, setEditLang] = React.useState('en');
  const langTabs = [
    { key: 'en', label: 'EN' },
    { key: 'zh', label: '繁中' },
    { key: 'cn', label: '简中' },
  ];

  const handleLangInput = (e, fieldId, lang) => {
    const { name, value } = e.target;
    handleInputChange({ target: { name, value }, _lang: lang }, fieldId);
  };

  const editLangLabelValue = data.i18n.label[editLang] || '';
  const editLangPlaceholderValue = (data.i18n.placeholder && data.i18n.placeholder[editLang]) || '';

  const renderInputLabel = () => {
    return (
      <div>
        <div className="RenderCustomInputFields-formLabel">
          {isEditMode ? t('label', 'Label') : labelValue}
        </div>
        {isEditMode && (
          <div>
            <div className="RenderCustomInputFields-langTabs">
              {langTabs.map(tab => (
                <div
                  key={tab.key}
                  className={`RenderCustomInputFields-langTab${editLang === tab.key ? ' active' : ''}`}
                  onClick={() => setEditLang(tab.key)}
                >
                  {tab.label}
                </div>
              ))}
            </div>
            <div className="RenderCustomInputFields-formInputContainer label">
              <input
                id={`label_${data.id}`}
                name="label"
                type="text"
                className="RenderCustomInputFields-formInput label"
                placeholder={`${t('label', 'Label')} (${editLang.toUpperCase()})`}
                disabled={!isEditable}
                value={editLangLabelValue}
                onChange={e => handleLangInput(e, data.id, editLang)}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderInputPlaceholder = () => {
    if (data.type === 'yesno') {
      return (
        <div>
          {isEditMode && (
            <div className="RenderCustomInputFields-formLabel">{t('preview', 'Preview')}</div>
          )}
          <div className="RenderCustomInputFields-formPreview">
            <span className="RenderCustomInputFields-previewToggle">{t('yesNoPreview', 'Yes / No')}</span>
          </div>
        </div>
      );
    }

    if (data.type === 'address') {
      return (
        <div>
          {isEditMode && (
            <div className="RenderCustomInputFields-formLabel">{t('preview', 'Preview')}</div>
          )}
          <div className="RenderCustomInputFields-formPreview">
            <div className="RenderCustomInputFields-previewAddress">
              <div className="RenderCustomInputFields-previewAddressField">{t('countryRegion', 'Country / Region')}</div>
              <div className="RenderCustomInputFields-previewAddressField" style={{ fontStyle: 'italic', fontSize: '11px', color: '#888' }}>
                {t('hkFields', 'HK: Region, District, Street, Building, Block, Floor, Flat')}
              </div>
              <div className="RenderCustomInputFields-previewAddressField" style={{ fontStyle: 'italic', fontSize: '11px', color: '#888' }}>
                {t('intlFields', 'Intl: Address Line 1 & 2, City, State, Postal Code')}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (data.type === 'dropdown') {
      const options = data.options || [];
      return (
        <div>
          {isEditMode && (
            <div className="RenderCustomInputFields-formLabel">{t('options', 'Options')}</div>
          )}
          {isEditMode ? (
            <div className="RenderCustomInputFields-optionsEditor">
              {options.map((opt, idx) => (
                <div key={opt.id} className="RenderCustomInputFields-optionRow">
                  <span className="RenderCustomInputFields-optionIndex">{idx + 1}.</span>
                  <input
                    type="text"
                    className="RenderCustomInputFields-optionInput"
                    placeholder={`Option ${idx + 1} (${editLang.toUpperCase()})`}
                    value={opt.label[editLang] || ''}
                    onChange={e => handleOptionLabelChange(data.id, opt.id, editLang, e.target.value)}
                  />
                  <div
                    className="RenderCustomInputFields-optionRemoveBtn"
                    onClick={() => handleRemoveOption(data.id, opt.id)}
                  >
                    &times;
                  </div>
                </div>
              ))}
              <div
                className="RenderCustomInputFields-addOptionBtn"
                onClick={() => handleAddOption(data.id)}
              >
                {t('addOption', '+ Add option')}
              </div>
            </div>
          ) : (
            <div className="RenderCustomInputFields-formPreview">
              <span className="RenderCustomInputFields-previewToggle">
                {options.length > 0
                  ? options.map(opt => opt.label[language] || opt.label.en || '').join(', ')
                  : 'No options defined'}
              </span>
            </div>
          )}
        </div>
      );
    }

    let inputView = (
      <div
        className={`
          RenderCustomInputFields-formInputContainer
          ${classInputDisabled}
        `}
      >
        <input
          type="text"
          name="placeholder"
          className="RenderCustomInputFields-formInput"
          placeholder={`${t('placeholder', 'Placeholder')} (${editLang.toUpperCase()})`}
          disabled={isInputDisabled}
          value={editLangPlaceholderValue}
          onChange={e => handleLangInput(e, data.id, editLang)}
        />
      </div>
    );

    if (data.type === 'textarea') {
      inputView = (
        <div
          className={`
            RenderCustomInputFields-textAreaFieldContainer
            ${classInputDisabled}
          `}
        >
          <textarea
            type="text"
            name="placeholder"
            className="RenderCustomInputFields-textAreaField"
            placeholder={`${t('placeholder', 'Placeholder')} (${editLang.toUpperCase()})`}
            disabled={isInputDisabled}
            value={editLangPlaceholderValue}
            onChange={e => handleLangInput(e, data.id, editLang)}
          />
        </div>
      );
    }

    return (
      <div>
        {isEditMode && (
          <div className="RenderCustomInputFields-formLabel">{t('placeholder', 'Placeholder')}</div>
        )}
        {inputView}
      </div>
    );
  };

  const renderDeleteBtn = () => {
    if (isEditable && isEditMode && !isArchived) {
      return (
        <div className="RenderCustomInputFields-formDeleteContainer">
          <div
            className="RenderCustomInputFields-formDeleteBtn"
            onClick={() => handleArchiveField(data.id, true)}
          >
            <div className="RenderCustomInputFields-formDeleteIcn" />
          </div>
        </div>
      );
    }

    return null;
  };

  const renderReceiptBtn = () => {
    if (!isArchived) {
      return (
        <div className="RenderCustomInputFields-formReceiptContainer">
          <div 
            className={`
              RenderCustomInputFields-formReceiptBtn 
              ${classInactive} 
              ${classActionDisabled} 
              ${classReceipt}
            `}
            onClick={() => handleActionField(data.id, 'showOnReceipt', !data.showOnReceipt)}
          >
            <div
              className={`
                RenderCustomInputFields-formReceiptIcn 
                ${classReceipt}
              `}
            />
          </div>
        </div>
      );
    }

    return null;
  };

  const renderRequiredDropdown = () => {
    return (
      <div className="RenderCustomInputFields-formDropdownContainer marginRight">
        <div 
          className={`
            RenderCustomInputFields-formDropdownBtn 
            ${classInactive} 
            ${classActionDisabled}
          `}
        >
          <div className="RenderCustomInputFields-formDropdownText">
            {data.required ? t('required', 'Required') : t('optional', 'Optional')}
          </div>
          {isEditable && isEditMode && (
            <Fragment>
              <div className="RenderCustomInputFields-formDropdownIcn" />
              <select
                value={data.required}
                className="RenderCustomInputFields-formDropdownSelect"
                onChange={e => handleActionField(data.id, 'required', e.target.value)}
              >
                <option value="true">{t('required', 'Required')}</option>
                <option value="false">{t('optional', 'Optional')}</option>
              </select>
            </Fragment>
          )}
        </div>
      </div>
    );
  };

  const renderVisibilityDropdown = () => {
    if (isEditable) {
      return (
        <div className="RenderCustomInputFields-formDropdownContainer marginRight">
          <div
            className={`
              RenderCustomInputFields-formDropdownBtn
              ${classInactive}
              ${classActionDisabled}
            `}
          >
            <div className="RenderCustomInputFields-formDropdownText">
              {data.internal ? t('internalOnly', 'Internal Only') : t('public', 'Public')}
            </div>
            {isEditMode && (
              <Fragment>
                <div className="RenderCustomInputFields-formDropdownIcn" />
                <select
                  value={data.internal ? 'true' : 'false'}
                  className="RenderCustomInputFields-formDropdownSelect"
                  onChange={e => handleActionField(data.id, 'internal', e.target.value === 'true')}
                >
                  <option value="false">{t('public', 'Public')}</option>
                  <option value="true">{t('internalOnly', 'Internal Only')}</option>
                </select>
              </Fragment>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  const typeLabels = {
    text: t('shortAnswer', 'Short Answer'),
    textarea: t('paragraphText', 'Paragraph'),
    yesno: t('yesNoType', 'Yes / No'),
    dropdown: t('dropdownType', 'Dropdown'),
    address: t('mailingAddress', 'Mailing Address'),
  };

  const renderTypeDropdown = () => {
    if (isEditable) {
      return (
        <div className="RenderCustomInputFields-formDropdownContainer">
          <div
            className={`
              RenderCustomInputFields-formDropdownBtn
              ${classInactive}
              ${classActionDisabled}
            `}
          >
            <div className="RenderCustomInputFields-formDropdownText">
              {typeLabels[data.type] || data.type}
            </div>
            {isEditMode && (
              <Fragment>
                <div className="RenderCustomInputFields-formDropdownIcn" />
                <select
                  value={data.type}
                  className="RenderCustomInputFields-formDropdownSelect"
                  onChange={e => handleActionField(data.id, 'type', e.target.value)}
                >
                  <option value="text">{t('shortAnswer', 'Short Answer')}</option>
                  <option value="textarea">{t('paragraphText', 'Paragraph')}</option>
                  <option value="yesno">{t('yesNoType', 'Yes / No')}</option>
                  <option value="dropdown">{t('dropdownType', 'Dropdown')}</option>
                  <option value="address">{t('mailingAddress', 'Mailing Address')}</option>
                </select>
              </Fragment>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  const renderRestoreBtn = () => {
    if (isEditMode && isArchived) {
      return (
        <div className="RenderCustomInputFields-formDeleteContainer">
          <div
            className="RenderCustomInputFields-formDeleteBtn restore"
            onClick={() => handleArchiveField(data.id, false)}
          >
            {t('restore', 'Restore')}
          </div>
        </div>
      );
    }

    return null;
  };

  const renderPermanentDeleteBtn = () => {
    if (isEditMode && isArchived) {
      return (
        <div className="RenderCustomInputFields-formDeleteContainer">
          <div
            className="RenderCustomInputFields-formDeleteBtn delete"
            onClick={() => handleDeleteField(data.id)}
          >
            {t('deletePermanently', 'Delete Permanently')}
          </div>
        </div>
      );
    }

    return null;
  };

  const renderArchivedText = () => {
    if (isEditMode && isArchived) {
      return (
        <div className="RenderCustomInputFields-formTextContainer">
          <div className="RenderCustomInputFields-formText">{t('fieldArchived', 'This field is archived')}</div>
        </div>
      );
    }

    return null;
  };

  return (
    <div
      id={data.id}
      className={`
        RenderCustomInputFields-formContainer 
        ${isEditMode ? "bordered" : ""} 
        ${!isEditable && isEditMode ? "disabled" : ""} 
        ${classArchived}
      `}
    >
      {renderOrderBtn()}

      <div className="RenderCustomInputFields-formInputGroup">
        {renderInputLabel()}
        {renderInputPlaceholder()}
      </div>
      
      <div
        className={`
          RenderCustomInputFields-formActionGroup 
          ${!isEditMode ? "marginTop" : ""}
        `}
      >
        <div className="RenderCustomInputFields-formActionContainer">
          <div className="RenderCustomInputFields-formActionGroupBtn">
            {renderRestoreBtn()}
            {renderPermanentDeleteBtn()}
            {renderDeleteBtn()}
            {renderReceiptBtn()}
          </div>
        
          {!isArchived && (
            <div className="RenderCustomInputFields-formActionGroupDropdown">
              {renderVisibilityDropdown()}
              {renderRequiredDropdown()}
              {renderTypeDropdown()}
            </div>
          )}
        </div>

        {renderArchivedText()}
      </div>
    </div>
  );
}

RenderCustomInputFields.propTypes = {
  func: PropTypes.object,
  state: PropTypes.object,
  language: PropTypes.string,
  languageObj: PropTypes.object,
};

RenderFormInput.propTypes = {
  language: PropTypes.string,
  languageObj: PropTypes.object,
  allData: PropTypes.array,
  data: PropTypes.object,
  isEditable: PropTypes.bool,
  isEditMode: PropTypes.bool,
  handleInputChange: PropTypes.func,
  handleArchiveField: PropTypes.func,
  handleDeleteField: PropTypes.func,
  handleActionField: PropTypes.func,
  handleOrderField: PropTypes.func,
  handleAddOption: PropTypes.func,
  handleRemoveOption: PropTypes.func,
  handleOptionLabelChange: PropTypes.func,
};

export default RenderCustomInputFields;
